import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { exportBoardBrief, fetchBoardBrief } from "../api/analyticsClient";

const EXPORTS = [
  { id: "pdf", label: "PDF" },
  { id: "docx", label: "DOCX" },
  { id: "pptx", label: "PPTX" },
  { id: "xlsx", label: "Excel" },
];

const actionButtonClass =
  "inline-flex h-9 items-center justify-center px-3.5 text-sm font-semibold leading-none rounded-lg transition-colors disabled:pointer-events-none";

function toBoardCompany(org) {
  if (!org || !org.name) return null;
  const company = {
    name: org.name,
    overall: org.overall,
    reportReady: org.reportReady,
    invited: org.invited,
    completed: org.completed,
    revenueLastYear: org.revenueLastYear,
    revenueLast12Months: org.revenueLast12Months,
    ebita: org.ebita,
    financial: org.financial,
    strategic: org.strategic,
    scalable: org.scalable,
    relentless: org.relentless,
    disciplined: org.disciplined,
    energized: org.energized,
  };
  if (Array.isArray(org.dimensions) && org.dimensions.length) {
    company.dimensions = org.dimensions.map((dimension) => ({
      label: dimension.label,
      score: dimension.score,
    }));
  }
  return company;
}

function renderInline(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

function BoardMarkdown({ markdown }) {
  const blocks = useMemo(() => {
    const lines = String(markdown || "").split("\n");
    const next = [];
    let list = null;
    const flush = () => {
      if (list) {
        next.push({ type: "ul", items: list });
        list = null;
      }
    };
    lines.forEach((line) => {
      if (line.startsWith("- ")) {
        list = list || [];
        list.push(line.slice(2));
        return;
      }
      flush();
      if (!line.trim()) return;
      if (line.startsWith("# ")) next.push({ type: "h1", text: line.slice(2) });
      else if (line.startsWith("## ")) next.push({ type: "h2", text: line.slice(3) });
      else if (line.trim() === "---") next.push({ type: "hr" });
      else next.push({ type: "p", text: line });
    });
    flush();
    return next;
  }, [markdown]);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-950/40 px-4 py-3">
      {blocks.map((block, index) => {
        if (block.type === "h1") {
          return (
            <h3 key={index} className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              {block.text}
            </h3>
          );
        }
        if (block.type === "h2") {
          return (
            <h4
              key={index}
              className="mt-4 mb-2 text-sm font-semibold uppercase tracking-[0.08em] text-brand dark:text-cyan-300"
            >
              {block.text}
            </h4>
          );
        }
        if (block.type === "ul") {
          return (
            <ul key={index} className="mb-2 space-y-2 pl-4 list-disc text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "hr") {
          return <hr key={index} className="my-3 border-slate-200 dark:border-slate-700" />;
        }
        return (
          <p key={index} className="mb-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function BoardBriefExportMenu({
  exporting,
  exportOpen,
  setExportOpen,
  menuRef,
  onExport,
  onCopy,
}) {
  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setExportOpen((open) => !open)}
        disabled={Boolean(exporting)}
        aria-haspopup="menu"
        aria-expanded={exportOpen}
        className={`${actionButtonClass} gap-1.5 text-brand dark:text-cyan-300 bg-brand/10 dark:bg-cyan-500/10 hover:bg-brand/20 dark:hover:bg-cyan-500/20 disabled:opacity-40`}
      >
        {exporting && exporting !== "share" ? "Exporting…" : "Export"}
        <svg className={`w-4 h-4 transition-transform ${exportOpen ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {exportOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-44 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1 shadow-lg"
        >
          <p className="px-3 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">
            Download
          </p>
          {EXPORTS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={Boolean(exporting)}
              onClick={() => onExport(item.id)}
              className="flex h-9 w-full items-center px-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-40"
            >
              {exporting === item.id ? "Exporting…" : item.label}
            </button>
          ))}
          <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
          <button
            type="button"
            role="menuitem"
            disabled={Boolean(exporting)}
            onClick={onCopy}
            className="flex h-9 w-full items-center px-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-40"
          >
            Copy
          </button>
        </div>
      ) : null}
    </div>
  );
}

function BoardBriefView({
  open,
  title,
  subtitle,
  markdown,
  exporting,
  exportOpen,
  setExportOpen,
  exportMenuRef,
  onClose,
  onShare,
  onExport,
  onCopy,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (exportOpen) {
        setExportOpen(false);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [exportOpen, onClose, open, setExportOpen]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="board-brief-view-title"
        tabIndex={-1}
        className="flex max-h-[min(90vh,820px)] w-full max-w-3xl flex-col overflow-visible rounded-2xl border border-slate-200 bg-white shadow-xl outline-none dark:border-slate-700 dark:bg-slate-900"
      >
        <div className="relative z-20 flex shrink-0 flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 id="board-brief-view-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {subtitle || "Prepared from the current portfolio view"}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              onClick={onShare}
              disabled={Boolean(exporting)}
              className={`${actionButtonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800`}
            >
              {exporting === "share" ? "Sharing…" : "Share"}
            </button>
            <BoardBriefExportMenu
              exporting={exporting}
              exportOpen={exportOpen}
              setExportOpen={setExportOpen}
              menuRef={exportMenuRef}
              onExport={onExport}
              onCopy={onCopy}
            />
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Close board brief"
            >
              ×
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          <BoardMarkdown markdown={markdown} />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function BoardBriefCard({ context, companies, anomalies, dataReady }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [exporting, setExporting] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const requestRef = useRef(0);
  const exportMenuRef = useRef(null);

  const payload = useMemo(() => {
    const mapped = (companies || []).slice(0, 150).map(toBoardCompany).filter(Boolean);
    return {
      ...(context || {}),
      companies: mapped.length ? mapped : context?.companies || [],
      anomalies: (anomalies || []).map((item) => ({
        type: item.type,
        severity: item.severity,
        title: item.title,
        detail: item.detail,
      })),
    };
  }, [anomalies, companies, context]);

  useEffect(() => {
    requestRef.current += 1;
    setBrief(null);
    setLoading(false);
    setViewOpen(false);
    setExportOpen(false);
  }, [payload]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onPointer = (event) => {
      if (!exportMenuRef.current?.contains(event.target)) setExportOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [exportOpen]);

  const closeView = () => {
    setExportOpen(false);
    setViewOpen(false);
  };

  const openView = () => {
    if (!brief?.markdown) return;
    setExportOpen(false);
    setViewOpen(true);
  };

  const scopeLine = brief?.scope_label && brief?.scope_text
    ? `${brief.scope_label}: ${brief.scope_text}`
    : brief?.scope_text || "";
  const viewSubtitle = [scopeLine, brief?.prepared_on ? `Prepared ${brief.prepared_on}` : ""]
    .filter(Boolean)
    .join(" · ");

  const prepare = async () => {
    if (!dataReady || loading) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    try {
      const response = await fetchBoardBrief(payload);
      const next = response?.data;
      if (!next?.markdown) {
        throw new Error("Board brief was empty");
      }
      if (requestRef.current === requestId) setBrief(next);
    } catch (error) {
      if (requestRef.current === requestId) {
        toast.error(error?.message || "Could not prepare the board brief");
      }
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  };

  const copyBrief = async () => {
    if (!brief?.markdown) return;
    try {
      await navigator.clipboard.writeText(brief.markdown);
      toast.success("Board brief copied");
    } catch {
      toast.error("Could not copy the brief");
    }
  };

  const exportBrief = async (format) => {
    if (!brief || exporting) return;
    setExporting(format);
    try {
      const blob = await exportBoardBrief(brief, format);
      downloadBlob(blob, `Board_Portfolio_Brief.${format}`);
      toast.success(`${format.toUpperCase()} ready`);
    } catch (error) {
      toast.error(error?.message || `Could not export ${format.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  const shareBrief = async () => {
    if (!brief?.markdown || exporting) return;
    const title = brief.title || "Board Portfolio Brief";
    setExporting("share");
    try {
      if (typeof navigator.share === "function") {
        let sharedFile = false;
        try {
          const blob = await exportBoardBrief(brief, "pdf");
          const file = new File([blob], "Board_Portfolio_Brief.pdf", { type: "application/pdf" });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ title, files: [file] });
            sharedFile = true;
          }
        } catch (error) {
          if (error?.name === "AbortError") return;
        }
        if (!sharedFile) {
          await navigator.share({ title, text: brief.markdown });
        }
        return;
      }
      const body = brief.markdown.length <= 1800 ? brief.markdown : `${brief.markdown.slice(0, 1600)}…`;
      window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
    } catch (error) {
      if (error?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(brief.markdown);
        toast.success("Sharing is not available here. The brief was copied instead.");
      } catch {
        toast.error("Could not share the brief");
      }
    } finally {
      setExporting("");
    }
  };

  return (
    <section
      className="mb-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 shadow-sm min-w-0"
      data-export-ignore
      aria-labelledby="board-brief-heading"
    >
      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h2 id="board-brief-heading" className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Board brief
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-3xl">
              Executive summary, milestones, and risks from the current portfolio view, written for a board pack.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={brief ? openView : prepare}
              disabled={loading || (!brief && !dataReady)}
              className={`${actionButtonClass} text-white bg-brand hover:bg-brand-light shadow-sm disabled:opacity-50`}
            >
              {loading ? "Preparing…" : brief ? "View" : "Prepare Board Brief"}
            </button>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {brief
              ? "The board brief is ready. Open it to review, share, or export."
              : loading
                ? "Preparing the board brief from the current portfolio view."
                : dataReady
                  ? "Prepare the brief when the dashboard reflects the portfolio you want in the board pack."
                  : "The brief is available after portfolio data loads."}
          </p>
        </div>
      </div>
      <BoardBriefView
        open={viewOpen && Boolean(brief?.markdown)}
        title={brief?.title || "Board Portfolio Brief"}
        subtitle={viewSubtitle}
        markdown={brief?.markdown || ""}
        exporting={exporting}
        exportOpen={exportOpen}
        setExportOpen={setExportOpen}
        exportMenuRef={exportMenuRef}
        onClose={closeView}
        onShare={() => {
          setExportOpen(false);
          shareBrief();
        }}
        onExport={(format) => {
          setExportOpen(false);
          exportBrief(format);
        }}
        onCopy={() => {
          setExportOpen(false);
          copyBrief();
        }}
      />
    </section>
  );
}
